import React from 'react';
import Header from '../components/layout/Header';
import { FileText, CheckCircle, XCircle, AlertTriangle, Trophy, Users, Gavel, Shield } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-10 h-10 text-blue-400" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Terms of Service
          </h1>
        </div>
        
        <div className="space-y-8 text-gray-300">
          <p className="text-sm text-gray-500">Last updated: January 2026</p>
          
          <section className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Welcome to CesarM Challenges</h2>
            <p className="mb-4">
              These Terms of Service ("Terms") govern your use of the CesarM Challenges platform 
              ("Platform", "Service", "we", "us", or "our"). By accessing or using our Platform, 
              you agree to be bound by these Terms. If you do not agree, please do not use our Service.
            </p>
            <p>
              CesarM Challenges is a gaming content platform where users can submit video clips 
              from their gameplay to compete in challenges and win prizes including cash, game codes, 
              and physical items.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              1. Eligibility Requirements
            </h2>
            <p className="mb-4">To participate in CesarM Challenges, you must:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Have a valid Twitch account in good standing</li>
              <li>Be at least 13 years old (or the minimum age required in your jurisdiction)</li>
              <li>If under 18, have parental or guardian consent to participate</li>
              <li>Not be currently banned or suspended from the Platform</li>
              <li>Reside in a jurisdiction where participation in skill-based competitions is legal</li>
              <li>Comply with all applicable local, state, national, and international laws</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              2. Challenge Participation Rules
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>One submission per challenge:</strong> Each user may submit only one entry per challenge unless otherwise specified</li>
              <li><strong>Original content only:</strong> All submissions must be your own original gameplay footage</li>
              <li><strong>Platform compliance:</strong> Videos must comply with Twitch and YouTube terms of service and community guidelines</li>
              <li><strong>Challenge-specific rules:</strong> Each challenge may have additional rules that must be followed</li>
              <li><strong>Submission deadlines:</strong> Submissions must be made before the challenge deadline; late entries will not be accepted</li>
              <li><strong>Admin discretion:</strong> Platform administrators have final authority on winner selection and rule interpretation</li>
              <li><strong>No manipulation:</strong> Any attempt to manipulate votes, views, or engagement metrics will result in disqualification</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              3. Prohibited Conduct
            </h2>
            <p className="mb-4">The following actions are strictly prohibited and may result in immediate account termination:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Submitting content you do not own or have proper rights to use</li>
              <li>Creating or using multiple accounts to gain unfair advantage</li>
              <li>Harassment, bullying, or abuse of other users or staff</li>
              <li>Attempting to hack, manipulate, or exploit the Platform</li>
              <li>Posting offensive, discriminatory, illegal, or inappropriate content</li>
              <li>Impersonating other users, streamers, or Platform staff</li>
              <li>Using cheats, hacks, or exploits in submitted gameplay footage</li>
              <li>Sharing account credentials or selling/transferring accounts</li>
              <li>Spamming, advertising, or promoting external services without permission</li>
              <li>Colluding with other users to manipulate challenge outcomes</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              4. Prizes and Payouts
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Prize display:</strong> Prize amounts and types are displayed on each challenge page</li>
              <li><strong>Winner notification:</strong> Winners will be contacted via their registered email or Twitch within 7 days of announcement</li>
              <li><strong>Payment methods:</strong> Cash prizes may be distributed via PayPal, bank transfer, or other methods at admin discretion</li>
              <li><strong>Physical prizes:</strong> Shipping for physical items is typically covered; international shipping may have restrictions</li>
              <li><strong>Game codes:</strong> Digital prizes will be delivered via email or direct message</li>
              <li><strong>Tax responsibility:</strong> Winners are solely responsible for reporting and paying any applicable taxes on prizes</li>
              <li><strong>Claim period:</strong> Prizes must be claimed within 30 days of winner announcement; unclaimed prizes are forfeited</li>
              <li><strong>Verification:</strong> We may require identity verification for prizes above certain thresholds</li>
              <li><strong>No substitution:</strong> Prizes cannot be exchanged for cash equivalents unless specified</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              5. Content Rights and Licensing
            </h2>
            <p className="mb-4">
              By submitting content to CesarM Challenges:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You retain full ownership of your original content</li>
              <li>You grant us a non-exclusive, royalty-free, worldwide license to display, distribute, and promote your submission on our Platform and social media channels</li>
              <li>You confirm that your content does not infringe on any third-party rights</li>
              <li>You understand that winning submissions may be featured in promotional materials</li>
              <li>You may request removal of your content at any time (this does not affect past promotional use)</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Gavel className="w-5 h-5 text-red-400" />
              6. Account Suspension and Termination
            </h2>
            <p className="mb-4">
              We reserve the right to suspend or terminate accounts at our discretion for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violation of these Terms of Service</li>
              <li>Fraudulent or suspicious activity</li>
              <li>Repeated rule violations across challenges</li>
              <li>Behavior that harms the community or Platform reputation</li>
            </ul>
            <p className="mt-4 text-yellow-400">
              <strong>Note:</strong> Banned users forfeit any pending prizes and are prohibited from creating new accounts. 
              Bans are applied to your Twitch ID and are permanent.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-400" />
              7. Disclaimers and Limitations
            </h2>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 space-y-4">
              <p>
                <strong>As-Is Service:</strong> CesarM Challenges is provided "as is" and "as available" without 
                warranties of any kind, express or implied.
              </p>
              <p>
                <strong>No Guarantee:</strong> We do not guarantee that the Platform will be uninterrupted, 
                error-free, or secure at all times.
              </p>
              <p>
                <strong>Limitation of Liability:</strong> To the maximum extent permitted by law, CesarM Challenges 
                and its operators shall not be liable for any indirect, incidental, special, consequential, or 
                punitive damages arising from your use of the Platform.
              </p>
              <p>
                <strong>Third-Party Content:</strong> We are not responsible for content hosted on third-party 
                platforms (Twitch, YouTube) linked through our Service.
              </p>
            </div>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Modifications to Terms</h2>
            <p>
              We may update these Terms at any time. Material changes will be announced on the Platform 
              and/or via email. Your continued use of the Service after changes are posted constitutes 
              acceptance of the modified Terms. We encourage you to review these Terms periodically.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws. 
              Any disputes arising from these Terms or your use of the Platform shall be resolved 
              through good-faith negotiation or, if necessary, binding arbitration.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Contact Information</h2>
            <p className="mb-4">
              For questions, concerns, or disputes regarding these Terms, please contact us through:
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://discord.gg/TsqcHNm647" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-600/30 transition-colors"
              >
                Discord Server
              </a>
              <a 
                href="https://x.com/CesarMChallenge" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-600/30 transition-colors"
              >
                Twitter/X
              </a>
            </div>
          </section>
          
          <div className="border-t border-gray-800 pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              By using CesarM Challenges, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
